import { format } from "date-fns";
import { useMemo, useState } from "react";
import { InvoiceTable } from "./InvoiceTable";
import PatientSearchAutocomplete from "../prescriptionUpload/PatientSearchAutocomplete";
import useInvoiceList from "../../controllers/invoice/useInvoiceList";
import { StringMap } from "../../models/ui/Path";
import CollectPaymentModal from "./CollectPaymentModal";
import Invoice from "../../models/domain/Invoice";
import HttpClient from "../../lib/HttpClient";

export default function InvoiceSummary() {
  const [status, setStatus] = useState("All");
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [selectedPatient, setSelectedPatient] = useState<any | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const [patientSearchName, setPatientSearchName] = useState("");

  const invoiceQuery = useMemo<StringMap>(() => {
    const query: StringMap = {};

    if (selectedPatient?.id) {
      query.patientId = String(selectedPatient.id);
    } else if (selectedDate) {
      query.date = selectedDate;
    }

    return query;
  }, [selectedPatient?.id, selectedDate]);

  const {
    resource: invoiceList,
    isLoading,
    refresh: refreshInvoiceList,
  } = useInvoiceList(invoiceQuery);

  const filteredInvoices = useMemo(() => {
    return invoiceList?.invoices?.filter((inv) => {
      const matchesStatus =
        status === "All"
          ? true
          : inv.payment_status.toLocaleLowerCase() ===
            status.toLocaleLowerCase();

      return matchesStatus;
    });
  }, [invoiceList, status]);

  const totals = useMemo(() => {
    return filteredInvoices?.reduce(
      (acc, inv) => {
        acc.billed += inv.total_amount;
        acc.paid += inv.paid_amount;
        acc.balance += inv.pending_amount;
        return acc;
      },
      { billed: 0, paid: 0, balance: 0 },
    );
  }, [filteredInvoices]);

  return (
    <div className="container py-4 mt-5 pt-5">
      {/* Header */}
      <h4 className="fw-semibold mb-4">
        Invoice Summary
        <span className="text-muted fw-normal ms-2">
          {format(new Date(), "dd MMM yyyy")}
        </span>
      </h4>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card shadow-sm border-0 p-3">
            <div className="text-uppercase small text-muted">Collected</div>
            <h3 className="fw-bold text-info mb-1">
              ₹{totals?.paid.toLocaleString()}
            </h3>
            <div className="text-muted small">
              {filteredInvoices?.length} invoices
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm border-0 p-3">
            <div className="text-uppercase small text-muted">Pending</div>
            <h3 className="fw-bold text-danger mb-1">
              ₹{totals?.balance.toLocaleString()}
            </h3>
            <div className="text-muted small">
              {
                filteredInvoices?.filter(
                  (i) => i.total_amount - i.paid_amount > 0,
                ).length
              }{" "}
              with balance
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row g-3 align-items-end mb-3">
        {/* DATE — hide when patient selected */}
        {!selectedPatient && (
          <div className="col-md-3">
            <label className="form-label small text-muted">Date</label>
            <input
              type="date"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        )}

        {/* PATIENT */}
        <div className="col-md-4">
          <label className="form-label small text-muted">Patient</label>

          {!selectedPatient ? (
            <PatientSearchAutocomplete
              patientName={patientSearchName}
              onNameChangeHandler={(name) => {
                setPatientSearchName(name);

                if (!name) {
                  setSelectedPatient(null);
                }
              }}
              onSelectHandler={(patient) => {
                setSelectedPatient(patient);
              }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 bg-light">
              <span className="fw-medium">{selectedPatient.name}</span>

              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={() => {
                  setSelectedPatient(null);
                  setPatientSearchName("");
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* STATUS */}
        <div className="col-md-2">
          <label className="form-label small text-muted">Status</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option>All</option>
            <option>Full</option>
            <option>Partial</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <InvoiceTable
        invoices={filteredInvoices ?? []}
        totals={totals ?? { billed: 0, paid: 0, balance: 0 }}
        onCollectClick={(invoice) => setSelectedInvoice(invoice)}
      />
      {selectedInvoice && (
        <CollectPaymentModal
          show={true}
          onClose={() => setSelectedInvoice(null)}
          onSave={async (data) => {
            if (!selectedInvoice) return;

            try {
              const newPaidAmount = selectedInvoice.paid_amount + data.amount;

              const newPendingAmount =
                selectedInvoice.total_amount - newPaidAmount;

              await HttpClient.performPost<any>(`/api/v1/users/invoice/`, {
                appointmentId: selectedInvoice.appointment_id,

                paymentMode: data.paymentMode,
                bankName: data.bankName ?? "",
                bankBranch: "",
                transactionId: data.transactionNo ?? "",
                chequeNo: "",
                cardChequeType: data.cardType ?? "",
                accountHolder: "",

                consultInvAmount: selectedInvoice.consult_inv_amount ?? 0,
                consultInvSGstAmount:
                  selectedInvoice.consult_inv_sgst_amount ?? 0,
                consultInvCGstAmount:
                  selectedInvoice.consult_inv_cgst_amount ?? 0,

                totalAmount: selectedInvoice.total_amount,
                totalSSGstAmount: selectedInvoice.total_sgst_amount ?? 0,
                totalCGstAmount: selectedInvoice.total_cgst_amount ?? 0,

                discount: selectedInvoice.discount ?? 0,

                paidAmount: newPaidAmount,
                pendingAmount: newPendingAmount,

                particulars: selectedInvoice.particulars,
              });
              // 🔥 Close modal
              setSelectedInvoice(null);

              refreshInvoiceList();
            } catch (error) {
              console.error("Payment collection failed", error);
            }
          }}
          invoiceNo={selectedInvoice.receipt_no}
          patientName={selectedInvoice.patient_name}
          totalBilled={selectedInvoice.total_amount}
          previouslyPaid={selectedInvoice.paid_amount}
        />
      )}
    </div>
  );
}
